import { HttpClient, RequestParams } from "../../utils/api/http-client";
import IBaseListingResponse from "../../utils/api/base-listing-response";
import { ILocationQuery } from "../../models/wms/location/query";
import { ILocation, ILocationDetails } from "../../models/wms/location/response";
import { IWmsDropdownItem } from "../../models/wms/dropdown/response";
import { IStore } from "../../models/store/response";
import { createActiveNamlieahStoreFilters } from "../../utils/helpers/store-filters";

class WmsSharedService<SecurityDataType = unknown> {
  http: HttpClient<SecurityDataType>;

  constructor(http: HttpClient<SecurityDataType>) {
    this.http = http;
  }

  // Locations
  getAllLocations = (query: ILocationQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<ILocation>>({
      path: "/wms/locations",
      method: "GET",
      query,
      ...params,
    });

  getLocation = (id: number, params?: RequestParams) =>
    this.http.request<ILocationDetails>({
      path: `/wms/locations/${id}`,
      method: "GET",
      ...params,
    });

  // Dropdown endpoints
  getDropdownStores = (params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IStore>>({
      path: "/store",
      method: "GET",
      query: {
        page: 1,
        perPage: -1,
        light: true,
        filters: createActiveNamlieahStoreFilters(),
      },
      ...params,
    });

  getDropdownZones = (query?: { 
    storeId?: number;
    zoneTypeId?: number;
  }, params?: RequestParams) => {
    const q: any = {
      page: 1,
      perPage: -1,
      light: true,
    };
    let filterIndex = 0;
    if (query?.storeId) {
      q[`filters[${filterIndex}][name]`] = "storeId";
      q[`filters[${filterIndex}][value]`] = query.storeId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.zoneTypeId) {
      q[`filters[${filterIndex}][name]`] = "zoneTypeId";
      q[`filters[${filterIndex}][value]`] = query.zoneTypeId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    return this.http.request<IBaseListingResponse<IWmsDropdownItem>>({
      path: "/wms/zones",
      method: "GET",
      query: q,
      ...params,
    });
  };

  getDropdownAisles = (query?: { 
    storeId?: number;
    zoneId?: number;
    zoneTypeId?: number;
    sides?: number;
  }, params?: RequestParams) => {
    const q: any = {
      page: 1,
      perPage: -1,
      light: true,
    };
    let filterIndex = 0;
    if (query?.storeId) {
      q[`filters[${filterIndex}][name]`] = "storeId";
      q[`filters[${filterIndex}][value]`] = query.storeId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.zoneId) {
      q[`filters[${filterIndex}][name]`] = "zoneId";
      q[`filters[${filterIndex}][value]`] = query.zoneId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.zoneTypeId) {
      q[`filters[${filterIndex}][name]`] = "zoneTypeId";
      q[`filters[${filterIndex}][value]`] = query.zoneTypeId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.sides) {
      q[`filters[${filterIndex}][name]`] = "sides";
      q[`filters[${filterIndex}][value]`] = query.sides;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    return this.http.request<IBaseListingResponse<IWmsDropdownItem>>({
      path: "/wms/aisles",
      method: "GET",
      query: q,
      ...params,
    });
  };

  getDropdownStorageUnits = (query?: { 
    storeId?: number;
    zoneId?: number;
    aisleId?: number;
    zoneTypeId?: number;
    storageUnitType?: number;
    side?: number;
  }, params?: RequestParams) => {
    const q: any = {
      page: 1,
      perPage: -1,
      light: true,
    };
    let filterIndex = 0;
    if (query?.storeId) {
      q[`filters[${filterIndex}][name]`] = "storeId";
      q[`filters[${filterIndex}][value]`] = query.storeId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.zoneId) {
      q[`filters[${filterIndex}][name]`] = "zoneId";
      q[`filters[${filterIndex}][value]`] = query.zoneId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.aisleId) {
      q[`filters[${filterIndex}][name]`] = "aisleId";
      q[`filters[${filterIndex}][value]`] = query.aisleId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.zoneTypeId) {
      q[`filters[${filterIndex}][name]`] = "zoneTypeId";
      q[`filters[${filterIndex}][value]`] = query.zoneTypeId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.storageUnitType) {
      q[`filters[${filterIndex}][name]`] = "storageUnitType";
      q[`filters[${filterIndex}][value]`] = query.storageUnitType;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.side) {
      q[`filters[${filterIndex}][name]`] = "side";
      q[`filters[${filterIndex}][value]`] = query.side;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    return this.http.request<IBaseListingResponse<IWmsDropdownItem>>({
      path: "/wms/storage-units",
      method: "GET",
      query: q,
      ...params,
    });
  };

  getDropdownShelves = (query?: { 
    storeId?: number;
    zoneId?: number;
    aisleId?: number;
    storageUnitId?: number;
    zoneTypeId?: number;
    hasBins?: boolean;
  }, params?: RequestParams) => {
    const q: any = {
      page: 1,
      perPage: -1,
      light: true,
    };
    let filterIndex = 0;
    if (query?.storeId) {
      q[`filters[${filterIndex}][name]`] = "storeId";
      q[`filters[${filterIndex}][value]`] = query.storeId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.zoneId) {
      q[`filters[${filterIndex}][name]`] = "zoneId";
      q[`filters[${filterIndex}][value]`] = query.zoneId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.aisleId) {
      q[`filters[${filterIndex}][name]`] = "aisleId";
      q[`filters[${filterIndex}][value]`] = query.aisleId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.storageUnitId) {
      q[`filters[${filterIndex}][name]`] = "storageUnitId";
      q[`filters[${filterIndex}][value]`] = query.storageUnitId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.zoneTypeId) {
      q[`filters[${filterIndex}][name]`] = "zoneTypeId";
      q[`filters[${filterIndex}][value]`] = query.zoneTypeId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.hasBins !== undefined) {
      q[`filters[${filterIndex}][name]`] = "hasBins";
      q[`filters[${filterIndex}][value]`] = query.hasBins;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    return this.http.request<IBaseListingResponse<IWmsDropdownItem>>({
      path: "/wms/shelves",
      method: "GET",
      query: q,
      ...params,
    });
  };

  getDropdownBins = (query?: { 
    storeId?: number;
    zoneId?: number;
    aisleId?: number;
    storageUnitId?: number;
    shelfId?: number;
    zoneTypeId?: number;
    binPosition?: number;
  }, params?: RequestParams) => {
    const q: any = {
      page: 1,
      perPage: -1,
      light: true,
    };
    let filterIndex = 0;
    if (query?.storeId) {
      q[`filters[${filterIndex}][name]`] = "storeId";
      q[`filters[${filterIndex}][value]`] = query.storeId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.zoneId) {
      q[`filters[${filterIndex}][name]`] = "zoneId";
      q[`filters[${filterIndex}][value]`] = query.zoneId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.aisleId) {
      q[`filters[${filterIndex}][name]`] = "aisleId";
      q[`filters[${filterIndex}][value]`] = query.aisleId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.storageUnitId) {
      q[`filters[${filterIndex}][name]`] = "storageUnitId";
      q[`filters[${filterIndex}][value]`] = query.storageUnitId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.shelfId) {
      q[`filters[${filterIndex}][name]`] = "shelfId";
      q[`filters[${filterIndex}][value]`] = query.shelfId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.zoneTypeId) {
      q[`filters[${filterIndex}][name]`] = "zoneTypeId";
      q[`filters[${filterIndex}][value]`] = query.zoneTypeId;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    if (query?.binPosition) {
      q[`filters[${filterIndex}][name]`] = "binPosition";
      q[`filters[${filterIndex}][value]`] = query.binPosition;
      q[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;
    }
    return this.http.request<IBaseListingResponse<IWmsDropdownItem>>({
      path: "/wms/bins",
      method: "GET",
      query: q,
      ...params,
    });
  };

  getDropdownZoneTypes = (params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IWmsDropdownItem>>({
      path: "/wms/zone-types",
      method: "GET",
      query: { page: 1, perPage: -1, light: true },
      ...params,
    });

  getDropdownLocations = (query: { storeId: number; zoneId?: number }, params?: RequestParams) => {
    const q: any = {
      page: 1,
      perPage: -1,
      light: true,
      "filters[0][name]": "storeId",
      "filters[0][value]": query.storeId,
      "filters[0][operation]": "eq",
    };
    if (query.zoneId) {
      q["filters[1][name]"] = "zoneId";
      q["filters[1][value]"] = query.zoneId;
      q["filters[1][operation]"] = "eq";
    }
    return this.http.request<IBaseListingResponse<IWmsDropdownItem>>({
      path: "/wms/locations",
      method: "GET",
      query: q,
      ...params,
    });
  };

  // Stock Count
  createStockCount = (data: { locationId: number; productId: number; physicalCount: number; reason?: string }, params?: RequestParams) =>
    this.http.request({
      path: "/wms/stock-count",
      method: "POST",
      body: data,
      ...params,
    });
}

export default WmsSharedService;
